import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { ButtonGroup } from '../components/ui/button-group';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tip } from '../components/ui/tip';
import { confirmDialog } from '../components/ConfirmDialog';
import { ArrowLeftIcon, PencilIcon, TrashIcon, TicketIcon, StarIcon } from '@heroicons/react/24/outline';

const actionButtonClass = 'size-8';
const actionSwitchContainerClass = 'flex h-8 items-center rounded-md border border-input bg-background px-2';
const actionSwitchClass = 'data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-gray-300 dark:data-[state=unchecked]:bg-gray-600';

// Reglas de cupones: los descuentos MAESTROS que definen condiciones y
// generan códigos (Google reseñas + plantillas ManyChat). Los códigos
// generados se ven en Fidelización → Cupones.
const CouponRules: React.FC = () => {
  const navigate = useNavigate();
  const [masters, setMasters] = useState<any[]>([]);
  const [generatedRocam, setGeneratedRocam] = useState(0);
  const [usedRocam, setUsedRocam] = useState(0);
  const [reviewCfg, setReviewCfg] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [viewingCodes, setViewingCodes] = useState<any>(null);
  const [codes, setCodes] = useState<any[]>([]);
  const [codesPag, setCodesPag] = useState({ page: 1, pages: 1, total: 0 });

  const fetchAll = useCallback(async () => {
    try {
      const [couponsRes, reviewsRes] = await Promise.all([
        axios.get('/api/coupons', { params: { page: 1, limit: 100 } }),
        axios.get('/api/reviews').catch(() => ({ data: {} }))
      ]);
      const all = couponsRes.data?.coupons || [];
      setMasters(all.filter((c: any) => c.is_template));
      const rocam = all.filter((c: any) => !c.is_template && String(c.code || '').startsWith('ROCAM-'));
      setGeneratedRocam(rocam.length);
      setUsedRocam(rocam.filter((c: any) => Number(c.current_uses) > 0).length);
      setReviewCfg(reviewsRes.data?.config || null);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error al cargar las reglas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleCoupon = async (id: string, enabled: boolean) => {
    try {
      await axios.patch(`/api/coupons/${id}`, { enabled });
      toast.success(`Regla ${enabled ? 'activada' : 'desactivada'}`);
      fetchAll();
    } catch { toast.error('Error actualizando la regla'); }
  };

  const deleteCoupon = async (id: string) => {
    if (!await confirmDialog('¿Eliminar esta regla? Los códigos ya generados no se tocan.')) return;
    try {
      await axios.delete(`/api/coupons/${id}`);
      toast.success('Regla eliminada');
      fetchAll();
    } catch { toast.error('Error eliminando la regla'); }
  };

  const resetGoogleRule = async () => {
    const ok = await confirmDialog({
      title: 'Restablecer regla de Google',
      description: 'Se pausa la campaña de reseñas y las reglas vuelven a los valores por defecto (10%, 90 días). Los cupones ya generados siguen vigentes.',
      confirmText: 'Restablecer',
      destructive: true
    });
    if (!ok) return;
    try {
      const res = await axios.put('/api/reviews/config', {
        enabled: false, courtesyType: 'percent', courtesyValue: 10, courtesyDays: 90, delayMinutes: 150,
        courtesyAppliesTo: 'all', courtesyMinAmount: 0, courtesyMaxDiscount: 0
      });
      setReviewCfg(res.data.config);
      toast.success('Regla de Google restablecida y campaña pausada');
    } catch (e: any) {
      toast.error(e.response?.data?.error || 'Error');
    }
  };

  const fetchCodes = async (couponId: string, page = 1) => {
    try {
      const res = await axios.get(`/api/coupons/${couponId}/generated-codes?page=${page}&limit=15`);
      setCodes(res.data.codes || []);
      setCodesPag(res.data.pagination || { page: 1, pages: 1, total: 0 });
    } catch { setCodes([]); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary" /></div>;
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" className="shrink-0" onClick={() => navigate(-1)}>
          <ArrowLeftIcon className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Reglas de cupones</h1>
          <p className="text-sm text-muted-foreground">Los descuentos maestros que definen condiciones y generan códigos. Los cupones generados se ven en Fidelización → Cupones.</p>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Origen</TableHead>
                <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Nombre</TableHead>
                <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Descuento</TableHead>
                <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Vigencia</TableHead>
                <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Generados</TableHead>
                <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Estado</TableHead>
                <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reviewCfg && (
                <TableRow>
                  <TableCell className="py-1 px-2 align-middle whitespace-nowrap">
                    <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400 border-amber-200 dark:border-amber-800"><StarIcon className="h-3 w-3 mr-1" />GOOGLE</Badge>
                  </TableCell>
                  <TableCell className="py-1 px-2 align-middle whitespace-nowrap">
                    <div className="font-medium text-foreground">Reseña de Google</div>
                  </TableCell>
                  <TableCell className="py-1 px-2 align-middle whitespace-nowrap font-medium">
                    {reviewCfg.courtesyType === 'amount' ? `$${Number(reviewCfg.courtesyValue || 0).toLocaleString('es-AR')}` : `${reviewCfg.courtesyValue}%`}
                  </TableCell>
                  <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-sm text-muted-foreground">{reviewCfg.courtesyDays}d</TableCell>
                  <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-xs text-muted-foreground">
                    <span className="text-violet-600 dark:text-violet-400 font-semibold">{generatedRocam}</span> gen · <span className="text-green-600 dark:text-green-400 font-semibold">{usedRocam}</span> usados
                  </TableCell>
                  <TableCell className="py-1 px-2 align-middle whitespace-nowrap">
                    <Badge variant={reviewCfg.enabled ? 'default' : 'secondary'}>{reviewCfg.enabled ? 'Activo' : 'Pausado'}</Badge>
                  </TableCell>
                  <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-right">
                    <div className="flex items-center justify-end">
                      <ButtonGroup>
                        <Tip label={reviewCfg.enabled ? 'Pausar campaña' : 'Activar campaña'}>
                          <div className={actionSwitchContainerClass}>
                            <Switch
                              checked={Boolean(reviewCfg.enabled)}
                              onCheckedChange={async (checked) => {
                                try {
                                  const res = await axios.put('/api/reviews/config', { ...reviewCfg, enabled: checked });
                                  setReviewCfg(res.data.config);
                                  toast.success(checked ? 'Campaña de reseñas activada' : 'Campaña de reseñas pausada');
                                } catch (e: any) {
                                  toast.error(e.response?.data?.error || 'Error');
                                }
                              }}
                              className={actionSwitchClass}
                            />
                          </div>
                        </Tip>
                        <Tip label="Editar reglas">
                          <Button variant="outline" size="icon" className={actionButtonClass} onClick={() => navigate('/reviews/config')}>
                            <PencilIcon className="h-4 w-4" />
                          </Button>
                        </Tip>
                        <Tip label="Restablecer y pausar">
                          <Button variant="outline" size="icon" className={`${actionButtonClass} text-muted-foreground hover:text-red-500`} onClick={resetGoogleRule}>
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </Tip>
                      </ButtonGroup>
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {masters.map((coupon: any) => (
                <TableRow key={coupon.id}>
                  <TableCell className="py-1 px-2 align-middle whitespace-nowrap">
                    <Badge className={`text-[10px] ${coupon.integration_type === 'manychat_welcome' ? 'bg-pink-100 text-pink-700 dark:bg-pink-900/20 dark:text-pink-400' : 'bg-violet-100 text-violet-700 dark:bg-violet-900/20 dark:text-violet-400'}`}>
                      {coupon.integration_type === 'manychat_welcome' ? 'MANYCHAT BIENVENIDA' : 'MANYCHAT CAMPAÑA'}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-1 px-2 align-middle whitespace-nowrap">
                    <div className="font-medium text-foreground truncate max-w-[220px]">{coupon.name}</div>
                  </TableCell>
                  <TableCell className="py-1 px-2 align-middle whitespace-nowrap font-medium">
                    {coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `$${coupon.discount_value}`}
                  </TableCell>
                  <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-sm text-muted-foreground">{coupon.expiration_days || 30}d</TableCell>
                  <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-xs text-muted-foreground">
                    <span className="text-violet-600 dark:text-violet-400 font-semibold">{coupon.generated_codes_count || 0}</span> gen · <span className="text-green-600 dark:text-green-400 font-semibold">{coupon.used_codes_count || 0}</span> usados
                  </TableCell>
                  <TableCell className="py-1 px-2 align-middle whitespace-nowrap">
                    <Badge variant={coupon.status === 'active' ? 'default' : 'secondary'}>
                      {coupon.status === 'active' ? 'Activo' : 'Desactivado'}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-right">
                    <div className="flex items-center justify-end">
                      <ButtonGroup>
                        <Tip label={coupon.enabled ? 'Desactivar' : 'Activar'}>
                          <div className={actionSwitchContainerClass}>
                            <Switch checked={!!coupon.enabled} onCheckedChange={(checked) => toggleCoupon(coupon.id, checked)} className={actionSwitchClass} />
                          </div>
                        </Tip>
                        {(coupon.generated_codes_count || 0) > 0 && (
                          <Tip label="Ver codigos">
                            <Button variant="outline" size="icon" className={actionButtonClass} onClick={() => { setViewingCodes(coupon); fetchCodes(coupon.id); }}>
                              <TicketIcon className="h-4 w-4" />
                            </Button>
                          </Tip>
                        )}
                        <Tip label="Editar">
                          <Button variant="outline" size="icon" className={actionButtonClass} onClick={() => navigate(`/discounts/coupons/${coupon.id}/edit`)}>
                            <PencilIcon className="h-4 w-4" />
                          </Button>
                        </Tip>
                        <Tip label="Eliminar">
                          <Button variant="outline" size="icon" className={`${actionButtonClass} text-muted-foreground hover:text-red-500`} onClick={() => deleteCoupon(coupon.id)}>
                            <TrashIcon className="h-4 w-4" />
                          </Button>
                        </Tip>
                      </ButtonGroup>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Modal: códigos generados por una plantilla ManyChat */}
      <Dialog open={!!viewingCodes} onOpenChange={(open) => { if (!open) setViewingCodes(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Codigos — {viewingCodes?.name}</DialogTitle>
          </DialogHeader>
          {codes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">No hay codigos generados aun</div>
          ) : (
            <>
              <div className="relative w-full max-h-[50vh] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Usuario</TableHead>
                      <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Codigo</TableHead>
                      <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Estado</TableHead>
                      <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Creado</TableHead>
                      <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Usos</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {codes.map((item: any) => (
                      <TableRow key={item.id}>
                        <TableCell className="py-1 px-2 align-middle text-xs">
                          {item.ig_name || ''} <span className="text-violet-500">@{item.ig_username}</span>
                        </TableCell>
                        <TableCell className="py-1 px-2 align-middle"><span className="font-mono font-bold tracking-wider">{item.code}</span></TableCell>
                        <TableCell className="py-1 px-2 align-middle text-xs capitalize">{item.status}</TableCell>
                        <TableCell className="py-1 px-2 align-middle text-xs text-muted-foreground">{new Date(item.created_at).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })}</TableCell>
                        <TableCell className="py-1 px-2 align-middle text-xs text-muted-foreground">{item.current_uses || 0}/{item.max_uses || 1}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {codesPag.pages > 1 && (
                <div className="flex items-center justify-between pt-2 border-t">
                  <span className="text-sm text-muted-foreground">{codesPag.total} codigos</span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={codesPag.page <= 1} onClick={() => viewingCodes && fetchCodes(viewingCodes.id, codesPag.page - 1)}>Anterior</Button>
                    <Button variant="outline" size="sm" disabled={codesPag.page >= codesPag.pages} onClick={() => viewingCodes && fetchCodes(viewingCodes.id, codesPag.page + 1)}>Siguiente</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CouponRules;
